import type { ReactNode } from 'react'

import { ArrowRight01Icon, Cancel01Icon } from '@hugeicons/react'
import clsx from 'clsx'

/* Faixa de aviso do sistema, no topo de uma tela.
 *
 * Não é `EmptyAlert` (estado vazio de lista) nem `CardHint` (dica dentro do
 * rail de um card): é um aviso dirigido a **esta** pessoa, que ocupa a largura
 * da tela, leva a algum lugar e some quando ela reconhece.
 *
 * Compacta e horizontal por restrição real, não por gosto. A conta a 375px:
 * 12px de padding de cada lado deixam 351px; ícone de 32 e o fechar de 28, com
 * os respectivos gaps, deixam 273px para o texto. Título e descrição cabem numa
 * linha cada, e por isso os dois truncam em vez de quebrar — aviso que cresce
 * em altura empurra o conteúdo da tela e deixa de ser aviso.
 *
 * Raio pela regra de proporção do §7 L1: é contêiner que agrupa controles, logo
 * o mesmo `rounded-[0.875rem]` do `Card` — não `rounded-full`, que pertence a
 * badge e chip. */

export type AnnouncementBannerTone = 'success' | 'brand'

export type AnnouncementBannerProps = {
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  tone?: AnnouncementBannerTone
  /** Ação do corpo. Sem ela a faixa é só informativa e não mostra a seta. */
  onOpen?: () => void
  /** Sem `onDismiss` o controle de fechar não aparece. */
  onDismiss?: () => void
  dismissLabel?: string
  className?: string
}

const TONE_SURFACE: Record<AnnouncementBannerTone, string> = {
  success: 'border-[color-mix(in_oklab,var(--color-success)_35%,white)] bg-[color-mix(in_oklab,var(--color-success)_10%,white)]',
  brand: 'border-[color-mix(in_oklab,var(--color-brand)_35%,white)] bg-[color-mix(in_oklab,var(--color-brand)_10%,white)]'
}

const TONE_ICON: Record<AnnouncementBannerTone, string> = {
  success: 'bg-[var(--color-success)]',
  brand: 'bg-[var(--color-brand)]'
}

export const AnnouncementBanner = ({
  title,
  description,
  icon,
  tone = 'success',
  onOpen,
  onDismiss,
  dismissLabel = 'Fechar aviso',
  className
}: AnnouncementBannerProps) => {
  const interactive = Boolean(onOpen)

  return (
    <div
      className={clsx(
        'flex items-center gap-2.5 rounded-[0.875rem] border px-3 py-2.5',
        TONE_SURFACE[tone],
        className
      )}
    >
      {icon && (
        <span
          className={clsx(
            'flex size-8 shrink-0 items-center justify-center rounded-full text-white',
            TONE_ICON[tone]
          )}
          aria-hidden
        >
          {icon}
        </span>
      )}

      {/* `min-w-0` no contêiner flexível: sem ele o filho com `truncate` não
          encolhe e a faixa estoura a largura da tela. */}
      <Body interactive={interactive} onOpen={onOpen}>
        <span className="truncate text-sm font-semibold text-gray-900">
          {title}
        </span>
        {description && (
          <span className="truncate text-2xs text-gray-700">{description}</span>
        )}
      </Body>

      {interactive && (
        <ArrowRight01Icon
          size={16}
          className="shrink-0 text-gray-600"
          aria-hidden
        />
      )}

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="flex size-7 shrink-0 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-black/5"
        >
          <Cancel01Icon size={15} aria-hidden />
        </button>
      )}
    </div>
  )
}

type BodyProps = {
  interactive: boolean
  onOpen?: () => void
  children: ReactNode
}

/* O corpo é botão quando leva a algum lugar, e `div` quando não leva — em vez
   de um `div` com `onClick`, que não recebe foco nem responde ao teclado. */
const Body = ({ interactive, onOpen, children }: BodyProps) => {
  const layout = 'flex min-w-0 flex-1 flex-col text-left'

  if (!interactive) return <div className={layout}>{children}</div>

  return (
    <button type="button" onClick={onOpen} className={layout}>
      {children}
    </button>
  )
}

AnnouncementBanner.displayName = 'AnnouncementBanner'
